import React, {
  useState, useEffect, useRef, useCallback,
} from 'react';
import { startProofRender, fetchProofStatus } from '../shared/api';
import { InlineSpinner } from '../shared/Spinner';
import ProofTextFields from './ProofTextFields';

// "Render true proof" (#4559).
//
// Every canvas in this designer — the editor, the thumbnails, Batch Test — is a
// separate implementation of the EC2 renderer, so none of them is authoritative.
// This panel asks the REAL renderer to render the season's live spec against a
// standing test student and shows what actually came back. It is the only
// output in the designer that is true by construction.
//
// The render lands in ~2 minutes, so a run is started and then polled.
const POLL_MS = 5000;

const STATUS_TEXT = {
  sent: 'Sent to the renderer — first poses usually land in about two minutes.',
  rendering: 'Rendering…',
  complete: 'Every pose the spec sells rendered.',
  partial: 'Stopped waiting. The poses below are everything that rendered.',
  error: 'Could not start.',
};

// A GIF proof answers a different question, so it says different things: the
// frames are a means, the animation is the result. The GIF is assembled after
// the poses, which is why "sent" promises a longer wait than the pose proof's.
const GIF_STATUS_TEXT = {
  ...STATUS_TEXT,
  sent: 'Sent to the renderer — frames first, then the GIF, usually about three minutes.',
  complete: 'The GIF rendered.',
  partial: 'Stopped waiting. Whatever rendered is below.',
};

const ProofPanel = ({ season, lookTemplateId }) => {
  const [run, setRun] = useState(null);
  const [error, setError] = useState(null);
  const [starting, setStarting] = useState(false);
  // Typed-in {{token}} values for the next run. Kept here rather than on the
  // look: this is throwaway test input ("what would a very long name do"),
  // not part of the design.
  const [textOverrides, setTextOverrides] = useState({});
  const timerRef = useRef(null);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  // Unmount (or a season switch) must not leave a poll running against a run
  // the user can no longer see.
  useEffect(() => clearTimer, []);

  const poll = useCallback((runId) => {
    fetchProofStatus(runId)
      .then((data) => {
        setRun(data);
        if (!data.finished) timerRef.current = setTimeout(() => poll(runId), POLL_MS);
      })
      .catch(() => {
        // A transient poll failure shouldn't kill the run — keep waiting.
        timerRef.current = setTimeout(() => poll(runId), POLL_MS);
      });
  }, []);

  const start = () => {
    clearTimer();
    setError(null);
    setStarting(true);
    startProofRender(season, lookTemplateId, textOverrides)
      .then((data) => {
        setRun(data);
        if (!data.finished) timerRef.current = setTimeout(() => poll(data.id), POLL_MS);
      })
      .catch((err) => {
        setError(err?.response?.data?.error || 'Could not start a proof render.');
        setRun(null);
      })
      .finally(() => setStarting(false));
  };

  const inFlight = starting || (run && !run.finished);
  const results = run?.results || [];

  return (
    <div style={{ borderTop: '1px solid #e0e0e0', background: '#fafafa', flexShrink: 0 }}>
      <div style={{
        padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
      }}
      >
        <button
          type="button"
          onClick={start}
          disabled={inFlight || !season}
          title={season
            ? 'Render this season\'s published spec on the real renderer'
            : 'Pick a season first'}
          style={{
            padding: '6px 12px',
            fontSize: 12,
            fontWeight: 600,
            border: '1px solid #2e7d32',
            borderRadius: 4,
            background: inFlight ? '#eee' : '#e8f5e9',
            color: inFlight ? '#999' : '#1b5e20',
            cursor: inFlight || !season ? 'default' : 'pointer',
          }}
        >
          {inFlight ? (
            <>
              <InlineSpinner size={12} />
              {' '}
              Rendering…
            </>
          ) : 'Render true proof'}
        </button>

        {run && (
          <span style={{ fontSize: 11, color: '#555' }}>
            {(run.gif_proof ? GIF_STATUS_TEXT : STATUS_TEXT)[run.status] || run.status}
            {run.expected_poses?.length > 0 && (
              <>
                {' '}
                <strong>
                  {run.produced_poses.length}
                  /
                  {run.expected_poses.length}
                </strong>
                {run.gif_proof ? ' frames' : ' poses'}
              </>
            )}
          </span>
        )}

        {run?.status === 'partial' && run.missing_poses?.length > 0 && (
          <span style={{ fontSize: 11, color: '#c62828' }}>
            {'never rendered: '}
            {run.missing_poses.join(', ')}
          </span>
        )}

        {error && <span style={{ fontSize: 11, color: '#c62828' }}>{error}</span>}
      </div>

      {/* Something the render could not carry — today, photos past the slide
          limit. It has to be said here: a dropped photo leaves the frame
          showing background art, which is indistinguishable from a frame that
          was meant to be empty. Amber, not red: the proof did render. */}
      {run?.message && run.status !== 'error' && (
        <div style={{ padding: '0 16px 8px', fontSize: 11, color: '#8a6116' }}>
          {run.message}
        </div>
      )}

      {/* The animation is the deliverable of a slide proof, so it leads —
          the poses below it are the frames it was built from. */}
      {run?.gif_proof && run.gif_url && (
        <div style={{ padding: '0 16px 12px' }}>
          <div style={{ fontSize: 11, color: '#666', marginBottom: 4 }}>
            The GIF as EC2 rendered it — every slide in the campaign:
          </div>
          <a href={run.gif_url} target="_blank" rel="noreferrer" title="Open full size">
            <img
              src={run.gif_url}
              alt="Rendered promo GIF"
              style={{
                maxWidth: 320, borderRadius: 4, border: '1px solid #ddd', display: 'block',
              }}
            />
          </a>
        </div>
      )}

      {run?.gif_proof && !run.gif_url && !run.finished && (
        <div style={{ padding: '0 16px 12px', fontSize: 11, color: '#666' }}>
          Frames first, then the GIF — it is assembled after the poses render.
        </div>
      )}

      {run?.gif_proof && !run.gif_url && run.status === 'partial' && (
        <div style={{ padding: '0 16px 12px', fontSize: 11, color: '#c62828' }}>
          The GIF never landed. The frames below are what rendered.
        </div>
      )}

      {results.length > 0 && (
        <div style={{
          display: 'flex', gap: 8, overflowX: 'auto', padding: '0 16px 12px',
        }}
        >
          {results.map((r) => {
            const isLookPose = run.look_pose != null && r.pose === run.look_pose;
            return (
              <a
                key={r.pose}
                href={r.url}
                target="_blank"
                rel="noopener noreferrer"
                title={`Pose ${r.pose} — open full size`}
                style={{
                  flexShrink: 0,
                  textDecoration: 'none',
                  textAlign: 'center',
                  border: `2px solid ${isLookPose ? '#1976d2' : '#ddd'}`,
                  borderRadius: 4,
                  padding: 3,
                  background: '#fff',
                }}
              >
                <img
                  src={r.url}
                  alt={`Pose ${r.pose}`}
                  style={{
                    display: 'block', height: 130, width: 'auto', background: '#f0f0f0',
                  }}
                />
                <span style={{
                  fontSize: 10,
                  color: isLookPose ? '#1976d2' : '#777',
                  fontWeight: isLookPose ? 700 : 400,
                }}
                >
                  {r.pose}
                  {isLookPose ? ' (this look)' : ''}
                </span>
              </a>
            );
          })}
        </div>
      )}

      <ProofTextFields
        season={season}
        values={textOverrides}
        onChange={setTextOverrides}
        disabled={inFlight}
      />

      {run && run.student_base && (
        <div style={{ padding: '0 16px 8px', fontSize: 10, color: '#999' }}>
          {`${run.source_count === 1 ? 'your reference photo' : `your ${run.source_count} reference photos`}`
            + ` as ${run.student_base} · rendered by EC2 block ${run.block_key} (spec ${run.spec_prefix}) · `
            + (run.draft_spec_key
              ? 'your SAVED look, published or not — save again and re-proof to see further edits'
              : 'the season\'s PUBLISHED spec')
            + ' · names and grades come from the fixture record, not the photo'}
          {Object.keys(run.text_overrides || {}).length > 0 && (
            <div style={{ color: '#1976d2' }}>
              {`rendered with your text: ${Object.entries(run.text_overrides)
                .map(([token, value]) => `${token.replace(/_/g, ' ')} = ${value === '' ? '(blank)' : value}`)
                .join(' · ')}`}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProofPanel;
